/** @typedef {import('../types/subscription').SubscriptionDetails} SubscriptionDetails */

function envTruthy(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return (
    normalized === "1" ||
    normalized === "true" ||
    normalized === "yes" ||
    normalized === "on"
  );
}

/** When true, hide plan banners and skip subscription gates for TC↔TEMA navigation. */
export function isSubscriptionFunctionalityOff() {
  const raw =
    process.env.REACT_APP_SUBSCRIPTION_FUNCTIONALITY_OFF ??
    process.env.SUBSCRIPTION_FUNCTIONALITY_OFF;
  if (raw === undefined || String(raw).trim() === "") {
    return true;
  }
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === "0" || normalized === "false" || normalized === "no" || normalized === "off") {
    return false;
  }
  return envTruthy(raw);
}

/** @type {SubscriptionDetails} */
export const EMPTY_SUBSCRIPTION = {
  hasActiveSubscription: 0,
  temaActive: 0,
  isTrial: false,
  isTrialExpired: false,
  planEndAt: "",
  daysRemaining: 0,
  tcBillingUrl: "",
  termId: "",
  amount: 0,
  currency: "",
  months: 0,
};

export function normalizeSubscription(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...EMPTY_SUBSCRIPTION };
  }
  return {
    hasActiveSubscription: Number(raw.hasActiveSubscription) === 1 ? 1 : 0,
    temaActive: Number(raw.temaActive) === 1 ? 1 : 0,
    isTrial: Boolean(raw.isTrial),
    isTrialExpired: Boolean(raw.isTrialExpired),
    planEndAt: String(raw.planEndAt || "").trim(),
    daysRemaining: Math.max(0, Number(raw.daysRemaining) || 0),
    tcBillingUrl: String(raw.tcBillingUrl || "").trim(),
    termId: String(raw.termId || "").trim(),
    amount: Math.max(0, Number(raw.amount) || 0),
    currency: String(raw.currency || "").trim(),
    months: Math.max(0, Number(raw.months) || 0),
  };
}

function formatPlanAmount(amount, currency) {
  const code = String(currency || "").trim();
  if (!code || amount <= 0) {
    return "";
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code.length === 3 ? code.toUpperCase() : code,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${code} ${amount}`;
  }
}

/** Human-readable plan line for purchase-success UI from API subscription fields. */
export function formatSubscriptionPlanLabel(subscription, fallbackLabel = "") {
  const sub = normalizeSubscription(subscription);
  let months = sub.months;
  if (!months && sub.termId) {
    if (sub.termId.endsWith("m")) {
      const parsed = Number.parseInt(sub.termId.slice(0, -1), 10);
      if (!Number.isNaN(parsed)) {
        months = parsed;
      }
    } else {
      const parsed = Number.parseInt(sub.termId, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        months = parsed;
      }
    }
  }
  const termPart = months
    ? `${months} month${months === 1 ? "" : "s"}`
    : sub.termId || "";
  const pricePart = formatPlanAmount(sub.amount, sub.currency);
  if (termPart && pricePart) {
    return `${termPart}, ${pricePart}`;
  }
  if (termPart) {
    return termPart;
  }
  if (pricePart) {
    return pricePart;
  }
  return fallbackLabel;
}

/** TutorCloud launch is allowed when Users.hasActiveSubscription is 1. */
export function canOpenTutorCloud(subscription) {
  if (isSubscriptionFunctionalityOff()) {
    return true;
  }
  return normalizeSubscription(subscription).hasActiveSubscription === 1;
}

/** Full iTEMA tool/workspace access when Users.hasActiveSubscription is 1. */
export function canUseTemaTools(subscription) {
  if (isSubscriptionFunctionalityOff()) {
    return true;
  }
  return normalizeSubscription(subscription).hasActiveSubscription === 1;
}

/** Paid plan row from API (termId / amount / months), not implicit trial. */
export function hasPaidPlanBillingDetails(subscription) {
  const sub = normalizeSubscription(subscription);
  return sub.amount > 0 || sub.months > 0 || Boolean(sub.termId);
}

/**
 * @returns {'trial' | 'purchase_success' | 'trial_ended' | null}
 */
export function resolveDashboardPlanBanner(subscription) {
  if (isSubscriptionFunctionalityOff()) {
    return null;
  }
  const sub = normalizeSubscription(subscription);
  if (sub.hasActiveSubscription !== 1) {
    return "trial_ended";
  }
  if (sub.isTrial) {
    return "trial";
  }
  if (hasPaidPlanBillingDetails(sub) || sub.planEndAt) {
    return "purchase_success";
  }
  return null;
}

export function daysUntilPlanEnd(subscription) {
  const sub = normalizeSubscription(subscription);
  if (sub.daysRemaining > 0) {
    return sub.daysRemaining;
  }
  const end = sub.planEndAt;
  if (!end) {
    return 0;
  }
  const endMs = Date.parse(end);
  if (Number.isNaN(endMs)) {
    return 0;
  }
  const diff = endMs - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function planSuccessDismissKey(teacherId) {
  return `tema_plan_success_dismissed_${String(teacherId || "").trim()}`;
}

export function isPlanSuccessDismissed(teacherId) {
  try {
    return localStorage.getItem(planSuccessDismissKey(teacherId)) === "1";
  } catch {
    return false;
  }
}

export function dismissPlanSuccess(teacherId) {
  try {
    localStorage.setItem(planSuccessDismissKey(teacherId), "1");
  } catch {
    // ignore
  }
}
